import { gql } from '@apollo/client';
import { FormLabel, Stack, useToast } from '@chakra-ui/react';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { toAvailableControlToggleOptions } from '~components/configurations/utils';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { ConfigControlLockState } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import {
  ConfigDisplayControlsToggle,
  ConfigToggleOptionsConfig,
} from '../ConfigDisplayControlsToggle';
import {
  ConfigDisplayInfraRedControlToggle_ConfigFragment,
  useConfigUpdateInfraRedControlMutation,
} from './__generated__/ConfigDisplayInfraRedControlToggle.graphql';

interface Props {
  config: ConfigDisplayInfraRedControlToggle_ConfigFragment;
  isDisabled?: boolean;
}

export function ConfigDisplayInfraRedControlToggle({ config, isDisabled = false }: Props) {
  const toast = useToast();
  const [updateInfraRedState, { loading }] = useConfigUpdateInfraRedControlMutation({});
  const { t } = useTranslation();
  const handleChange = async (controlState: ConfigControlLockState) => {
    const { id } = config;

    try {
      updateInfraRedState({
        variables: {
          input: {
            configurationId: id,
            controlState,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
    } catch (error) {
      toast({
        status: 'error',
        title: t('controlUpdateErr'),
        description: fromError(error, 'UpdateInfraRedControl'),
      });
    }
  };

  const infraRedControlToggleOptions: Array<ConfigToggleOptionsConfig<ConfigControlLockState>> = [
    { value: ConfigControlLockState.Locked, label: t('lockAllInputs') },
    { value: ConfigControlLockState.VolumeOnly, label: t('allowVolumeChange') },
    { value: ConfigControlLockState.PowerOnly, label: t('allowPowerChange') },
    { value: ConfigControlLockState.Unlocked, label: t('unlockAllInputs') },
  ];
  const currentControlState = config.infraRedControl?.reported;

  const controlOptions = toAvailableControlToggleOptions(
    infraRedControlToggleOptions,
    config.infraRedControl?.supportedValues || [],
  );

  if (isNil(currentControlState)) {
    return null;
  }

  return (
    <Stack direction="column" spacing="3">
      <FormLabel>
        {t('lockRemoteControl')}
        <FormLabelPendingIndicator isPending={loading} />
      </FormLabel>

      <ConfigDisplayControlsToggle
        name="infraRedControlState"
        value={currentControlState}
        handleChange={handleChange}
        isLoading={loading}
        options={controlOptions}
        isDisabled={isDisabled}
      />
    </Stack>
  );
}

ConfigDisplayInfraRedControlToggle.graphql = {
  fragments: {
    ConfigDisplayInfraRedControlToggle_config: gql`
      fragment ConfigDisplayInfraRedControlToggle_config on ConfigurationModel {
        id
        infraRedControl {
          reported
          supportedValues
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateInfraRedControl: gql`
      mutation ConfigUpdateInfraRedControl($input: ConfigurationUpdateInfraRedControlInput!) {
        configurationUpdateInfraRedControl(input: $input) {
          id
          infraRedControl {
            reported
            supportedValues
          }
        }
      }
    `,
  },
};
